import { useCallback, useEffect, useState } from "react";
import { useBrainApi } from "../../api/brainApi";
import {
  PageTitle,
  Panel,
  AlertBanner,
  selectClass,
  btnPrimary,
} from "../../components/controlPlaneUi";

// Anthropic model ids selectable per output type. Docs vs. requirements can use different models
// (e.g. a cheaper model for docs, a stronger one for requirements). Empty = fall back to the
// engine's ANTHROPIC_MODEL default.
const MODEL_OPTIONS: { value: string; label: string }[] = [
  { value: "", label: "Default (engine ANTHROPIC_MODEL)" },
  { value: "claude-opus-4-8", label: "Claude Opus 4.8 (most capable)" },
  { value: "claude-sonnet-4-6", label: "Claude Sonnet 4.6 (balanced)" },
  { value: "claude-haiku-4-5", label: "Claude Haiku 4.5 (fast / cheap)" },
];

const ModelSelect: React.FC<{
  label: string;
  hint: string;
  value: string;
  onChange: (v: string) => void;
}> = ({ label, hint, value, onChange }) => {
  // Show an unknown stored value (e.g. a model not in the list) as an extra option.
  const known = MODEL_OPTIONS.some((o) => o.value === value);
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      <select className={selectClass} value={value} onChange={(e) => onChange(e.target.value)}>
        {MODEL_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
        {!known && <option value={value}>{value} (current)</option>}
      </select>
      <p className="mt-1 text-[11px] text-slate-400">{hint}</p>
    </div>
  );
};

const LLMConfigPage: React.FC = () => {
  const api = useBrainApi();
  const [docsModel, setDocsModel] = useState("");
  const [requirementsModel, setRequirementsModel] = useState("");
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await api.getConfig();
      setDocsModel(cfg.docsModel ?? "");
      setRequirementsModel(cfg.requirementsModel ?? "");
      setUpdatedAt(cfg.updatedAt);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "failed to load config");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const cfg = await api.updateConfig({
        docsModel: docsModel || null,
        requirementsModel: requirementsModel || null,
      });
      setUpdatedAt(cfg.updatedAt);
      setNotice("Model configuration saved.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageTitle kind="Config" name="llm-config" />
      {error && <AlertBanner variant="error">{error}</AlertBanner>}
      {notice && <AlertBanner variant="ok">{notice}</AlertBanner>}

      <Panel title="Models">
        {loading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : (
          <div className="max-w-md space-y-4">
            <ModelSelect
              label="Documentation model"
              hint="Used for the sync (transcripts → wiki) and source (code/docs → wiki/codebase) workflows."
              value={docsModel}
              onChange={setDocsModel}
            />
            <ModelSelect
              label="Requirements model"
              hint="Used for gap analysis and requirements generation."
              value={requirementsModel}
              onChange={setRequirementsModel}
            />
            <div className="flex items-center gap-3 pt-1">
              <button type="button" className={btnPrimary} disabled={saving} onClick={save}>
                {saving ? "Saving…" : "Save"}
              </button>
              {updatedAt && (
                <span className="text-[11px] text-slate-400">
                  Last updated {new Date(updatedAt).toLocaleString()}
                </span>
              )}
            </div>
          </div>
        )}
      </Panel>
    </div>
  );
};

export default LLMConfigPage;
